import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Lock, ArrowRight, CheckCircle, ExternalLink, ShieldCheck, Loader2 } from "lucide-react";

// ──────────────────────────────────────────────────────────────────
// The Zoho registration URL is stored as an AES-256-GCM encrypted
// blob (base64). The encryption key is derived from the password
// via PBKDF2 (100 000 iterations, SHA-256).
//
// ✅ The password NEVER appears in this source code.
// ✅ The URL NEVER appears in this source code.
// ✅ Without the correct password, the URL cannot be recovered.
//
// Format: base64( salt[16] || iv[12] || ciphertext[…] )
// ──────────────────────────────────────────────────────────────────
const ENCRYPTED_LINK =
  "n29a3trT9qTiforcUAUSdxmiSgVjAYAyjDMf4fGumcjOkmfnAFyNRIvTLhP9hefWQ1FwK+c96n4vwm24G0dFXL8uR5XBycDZhC3Su8vBj4zKe43sakGhwaikJNAG1txpWA7YFD/+e4ong7bQbLgW7iHXuU0veHCHDk3qUuwSjMsK8dA=";

/**
 * Decrypt the encrypted link using the user-provided password.
 * Returns the plaintext URL on success, or null on failure.
 */
async function decryptLink(password) {
  try {
    const encoder = new TextEncoder();
    const decoder = new TextDecoder();

    // Decode the base64 blob
    const combined = Uint8Array.from(atob(ENCRYPTED_LINK), (c) => c.charCodeAt(0));

    // Extract salt (16 bytes), iv (12 bytes), ciphertext (rest)
    const salt = combined.slice(0, 16);
    const iv = combined.slice(16, 28);
    const ciphertext = combined.slice(28);

    // Derive AES-256-GCM key from password via PBKDF2
    const keyMaterial = await crypto.subtle.importKey(
      "raw",
      encoder.encode(password),
      "PBKDF2",
      false,
      ["deriveKey"]
    );

    const key = await crypto.subtle.deriveKey(
      { name: "PBKDF2", salt, iterations: 100000, hash: "SHA-256" },
      keyMaterial,
      { name: "AES-GCM", length: 256 },
      false,
      ["decrypt"]
    );

    // Attempt decryption — will throw if password is wrong
    const plainBuffer = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      ciphertext
    );

    return decoder.decode(plainBuffer);
  } catch {
    // Decryption failed → wrong password
    return null;
  }
}

const ZohoForStartups = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [zohoRegistrationUrl, setZohoRegistrationUrl] = useState("");

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    const url = await decryptLink(password);

    if (url) {
      setZohoRegistrationUrl(url);
      setIsLoggedIn(true);
    } else {
      setError("Invalid password. Please try again.");
    }

    setIsLoading(false);
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen pt-24 pb-12 bg-slate-50 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-100"
        >
          <div className="p-8 sm:p-10">
            <div className="flex justify-center mb-6">
              <div className="bg-blue-100 p-4 rounded-full text-blue-600">
                <ShieldCheck size={32} />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-center text-slate-900 mb-2">
              Secure Access
            </h2>
            <p className="text-slate-500 text-center mb-8 text-sm">
              Enter the password shared with TBIF startups to access the Zoho for Startups portal.
            </p>

            <form onSubmit={handleLogin} className="space-y-6">
              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm text-center border border-red-100">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Enter access password"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-blue-600/20 transition-all flex items-center justify-center gap-2 group"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    Unlock Access
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 bg-slate-50 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-blue-600/10 to-transparent -z-10" />
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl -z-10" />
      <div className="absolute top-48 -left-24 w-72 h-72 bg-purple-400/10 rounded-full blur-3xl -z-10" />

      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 overflow-hidden border border-slate-100"
        >
          {/* Header Section */}
          <div className="p-8 sm:p-12 border-b border-slate-100 bg-white relative">
            <div className="flex flex-col md:flex-row items-center gap-8 justify-between">
              <div className="flex-1 space-y-4 text-center md:text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-50 text-green-600 rounded-full text-sm font-semibold mb-2 border border-green-100">
                  <CheckCircle size={16} />
                  Access Granted
                </div>
                <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
                  Zoho for Startups
                </h1>
                <p className="text-lg text-slate-600 max-w-2xl">
                  Empowering your startup journey with the tools and resources you need to scale effortlessly.
                </p>
              </div>

              {/* Zoho Logo placeholder using a standard high-quality image from Zoho's CDN */}
              <div className="w-48 h-auto flex-shrink-0 bg-white p-4 rounded-2xl shadow-sm border border-slate-50">
                <img
                  src={process.env.PUBLIC_URL + "/Zoho_for_Startups_logo.png"}
                  alt="Zoho Logo"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-8 sm:p-12 bg-slate-50/50">
            <div className="grid md:grid-cols-2 gap-10">

              {/* Program Overview */}
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-slate-900">Program Overview</h3>
                <p className="text-slate-600 leading-relaxed">
                  IIT Ropar TBIF has partnered with Zoho to provide our incubated and associated startups with world-class software solutions. Zoho offers a comprehensive suite of business applications to help you manage your operations, sales, marketing, and finances from a single platform.
                </p>
                <ul className="space-y-3">
                  {["Access to 40+ integrated applications", "Dedicated startup support",].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-slate-600">
                      <div className="mt-1 bg-blue-100 text-blue-600 rounded-full p-0.5">
                        <CheckCircle size={14} />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Key Benefit & CTA */}
              <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 flex flex-col justify-center relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -z-10" />

                <div className="space-y-6 text-center md:text-left">
                  <div>
                    <h4 className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-2">Exclusive Benefit</h4>
                    <div className="text-3xl font-extrabold text-slate-900">
                      Up to ₹1.86 Lakhs
                    </div>
                    <p className="text-slate-500 mt-2 font-medium">
                      in wallet credits for one year.*
                    </p>
                  </div>

                  <div className="pt-6 border-t border-slate-100">
                    <a
                      href={zohoRegistrationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex w-full md:w-auto items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-4 px-8 rounded-xl shadow-lg shadow-blue-600/25 transition-all hover:-translate-y-0.5"
                    >
                      Register Now
                      <ExternalLink size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </a>
                    <p className="text-xs text-slate-400 mt-4 text-center md:text-left">
                      *Subject to eligibility and terms of the Zoho for Startups program.
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ZohoForStartups;
